import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { MaterialSelect } from "@/components/slot/MaterialSelect";
import { KovanieCategoryPicker } from "@/components/dielec/KovanieCategoryPicker";
import { VyberDialog } from "@/components/vyber/VyberDialog";
import { useCreateDielec } from "@/api/hooks/useDielce";
import { useHromadneZostavy } from "@/api/hooks/useHromadneZostavy";
import { useNazvyDielcov } from "@/api/hooks/useNazvyDielcov";
import { useSplitRules } from "@/api/hooks/useSplitRules";
import { useHrubkyDosiek } from "@/api/hooks/useCennik";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { kodVrstvy, suggestNextKod } from "@/lib/dielecKod";
import { normalizeNum, parseDecimal, sklonuj } from "@/lib/format";
import { comboKey, pravidloPreHrubku, trieda } from "@/lib/splitRules";
import { predvolenyNazovKovania } from "@/lib/kategorie";
import type {
  Dielec,
  Material,
  MaterialSlot,
  SplitRule,
} from "@/api/schemas";
import { OPERACIA_LEPENA_VRSTVA } from "@/lib/operacie";
import { X } from "lucide-react";

const NO_SLOT = "__none__";

/**
 * Kovanie v zozname pod tabuľkou (KOLO 226 — user: „viacero kovaní naraz
 * vložiť do zostavy, nie len jedno"). Počet a dĺžka ako text — pole sa
 * smie na chvíľu vymazať.
 */
interface KovanieVZozname {
  /** Kľúč riadku — kovanie na metre môže byť v zozname dvakrát s inou dĺžkou */
  kluc: string;
  material: Material;
  nazov: string;
  pocet: string;
  dlzka: string;
}

/** `A5`, `A6`, … — návrhy kódov za sebou (finálne pridelí server). */
function kodyZaSebou(zostavaKod: string, existing: Dielec[], n: number): string[] {
  const prvy = suggestNextKod(zostavaKod, existing);
  const start = Number(prvy.slice(zostavaKod.length));
  return Array.from({ length: n }, (_, i) => `${zostavaKod}${start + i}`);
}

/** Štandardné hrúbky — pre ne sa split prompt NEZOBRAZUJE.
 * (To je Tandem default; ak user pridá split rule pre 18mm, pravidlo
 * z DB má prednosť a prompt sa zobrazí.) */
const STANDARD_HRUBKY = new Set([10, 12, 16, 18, 19, 25]);

/**
 * Druh dielca určuje set polí vo formulári + kde sa berie materiál:
 *
 * - `plosny` — Pole X×Y×Z + 4 hrany (slot ALEBO direct material druh=plosny)
 * - `dlzkovy` — Pole X (dĺžka) + materiál (direct, druh=dlzkovy — hranol)
 * - `cenovka` — ten istý dĺžkový riadok, materiál je **hrana** (KOLO 169):
 *   cenovka sa reže z hrany, takže sa objednáva a účtuje ako hrana
 *   (backend ju spozná podľa sekcie materiálu — `je_riadok_z_hrany`)
 * - `kusovy` — Iba pocet_ks + materiál (direct, druh=kusovy — kovanie, nohy)
 */
type DielecDruh = "plosny" | "dlzkovy" | "cenovka" | "kusovy";

/** Labely zhodné s cenníkom (Plošné materiály / Dĺžkový materiál / Kovanie). */
const DRUH_LABELS: Record<DielecDruh, string> = {
  plosny: "Plošné materiály (DTD/MDF, X×Y×Z + hrany)",
  dlzkovy: "Dĺžkový materiál (hranol, pracovná doska)",
  cenovka: "Cenovka (z hrany)",
  kusovy: "Kovanie (kovanie, nohy, podpierky)",
};

/** Názov cenovky, keď ho user nevyplní. */
const NAZOV_CENOVKY = "Cenovka";


const schema = z.object({
  nazov_strojovy: z.string().max(100).optional().or(z.literal("")),
  pocet_ks: z.coerce.number().int().min(1).default(1),
  x_mm: z.string().optional().or(z.literal("")),
  y_mm: z.string().optional().or(z.literal("")),
  z_mm: z.string().optional().or(z.literal("")),
});

type FormData = z.infer<typeof schema>;

export interface DielecCreateDialogProps {
  zakazkaId: string;
  zostavaId: string;
  zostavaKod: string;
  existingDielce: Dielec[];
  sloty: MaterialSlot[];
  /**
   * Odkiaľ je okno otvorené: „kovanie" = len kusový druh (Pridať ▾ →
   * Kovanie), „cenovka" = len cenovka z hrany (KOLO 197 — dovtedy sa k nej
   * nedalo dostať, editor otváral okno len ako kovanie), „dielec" = plošný,
   * dĺžkový aj cenovka.
   */
  rezim?: "dielec" | "kovanie" | "cenovka";
  /**
   * Ďalšie vybrané zostavy (KOLO 196, kovanie a od KOLA 197 cenovka) — ten
   * istý riadok pribudne aj do nich, celé jedným krokom späť. User:
   * *„vybrať zostavy a vložiť do nich rovnaké kovanie"*.
   */
  dalsieZostavy?: { id: string; kod: string; existingDielce: Dielec[] }[];
  /**
   * Prepínač zákazky „rozmery bez hrán" (KOLO 131): zadaný rozmer plošného
   * dielca je holá doska, backend k nemu pripočíta hrany.
   */
  rozmeryBezHran?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DielecCreateDialog({
  zakazkaId,
  zostavaId,
  zostavaKod,
  existingDielce,
  sloty,
  rezim = "dielec",
  dalsieZostavy = [],
  rozmeryBezHran = false,
  open,
  onOpenChange,
}: DielecCreateDialogProps): JSX.Element {
  const createMutation = useCreateDielec(zakazkaId, zostavaId);
  const hromadne = useHromadneZostavy(zakazkaId);
  const viacZostav = rezim !== "dielec" && dalsieZostavy.length > 0;
  const kodyZostav = [zostavaKod, ...dalsieZostavy.map((z) => z.kod)];
  const allowedDruhy: readonly [DielecDruh, ...DielecDruh[]] =
    rezim === "kovanie"
      ? (["kusovy"] as const)
      : rezim === "cenovka"
        ? (["cenovka"] as const)
        : (["plosny", "dlzkovy", "cenovka"] as const);
  // „kovanie" / „cenovku" v titulku a hláškach
  const coPridava = rezim === "cenovka" ? "cenovku" : "kovanie";
  const [druh, setDruh] = useState<DielecDruh>(allowedDruhy[0]);
  const [slotId, setSlotId] = useState<string>(NO_SLOT);
  const [vybranyMaterialId, setDirectMaterialId] = useState<string | null>(null);
  /**
   * Vybrané kovanie sa predáva na metre (profil, koľajnica, kefa —
   * dĺžkový materiál, KOLO 176). Riadok potom potrebuje dĺžku: kráti sa
   * z tyče v nárezovom pláne a bez nej by stál 0 €.
   */
  const [kovanieNaMetre, setKovanieNaMetre] = useState(false);
  const [tycKovania, setTycKovania] = useState<number | null>(null);
  const [zoznam, setZoznam] = useState<KovanieVZozname[]>([]);
  // Označené kovanie (klik) — pre tlačidlo Do zoznamu bez dvojkliku
  const [oznacene, setOznacene] = useState<Material | null>(null);
  const [ukladaZoznam, setUkladaZoznam] = useState(false);
  // Pole dĺžky, na ktoré skočiť po pridaní kovania na metre do zoznamu
  const [fokusDlzky, setFokusDlzky] = useState<string | null>(null);
  useEffect(() => {
    if (!fokusDlzky) return;
    document.getElementById(`dc-zoznam-dlzka-${fokusDlzky}`)?.focus();
    setFokusDlzky(null);
  }, [fokusDlzky]);
  // Cenovka je dĺžkový riadok — rovnaké polia, len materiál je hrana
  const dlzkovyRiadok = druh === "dlzkovy" || druh === "cenovka";

  /** Hranol vybraný pre dĺžkový riadok nesmie prejsť do cenovky (a naopak). */
  function zmenDruh(novy: DielecDruh): void {
    setDruh(novy);
    setDirectMaterialId(null);
  }
  // Pre split prompt: "default" (prvá kombinácia), "alt:18+18" (konkrétna),
  // alebo "masivny" (jeden masívny dielec).
  const [splitChoice, setSplitChoice] = useState<string>("default");

  // Prednastavené názvy dielcov (Nastavenia) — datalist pre Názov
  const nazvyQuery = useNazvyDielcov();
  const nazvyDielcov = nazvyQuery.data?.data ?? [];
  const splitRulesQuery = useSplitRules();
  const allRules = splitRulesQuery.data?.data ?? [];
  // Hrúbky dosiek v cenníku — pre rozhodnutie či "masívny" dáva zmysel.
  const hrubkyQuery = useHrubkyDosiek("plosny");
  // Podľa triedy (KOLO 188) — doska 18,7 je 18, nie 19
  const dostupneHrubky = new Set(
    (hrubkyQuery.data?.data ?? []).map((v) => trieda(parseFloat(v))),
  );

  const {
    register,
    handleSubmit,
    reset,
    control,
    getValues,
    setValue,
    formState: { isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      nazov_strojovy: "",
      pocet_ks: 1,
      x_mm: "",
      y_mm: "",
      z_mm: "",
    },
  });

  // Watch z_mm pre delenie hrúbky promptu
  const watchedZ = useWatch({ control, name: "z_mm" }) ?? "";
  const zNum = trieda(parseFloat(watchedZ));
  // Split prompt sa zobrazí keď: druh=plosny, Z je valídne, Z nie je
  // štandardná hrúbka a buď existuje pravidlo alebo sa nedá vyrobiť masívne.
  const splitRule =
    druh === "plosny" && Number.isFinite(zNum)
      ? pravidloPreHrubku(allRules, watchedZ)
      : null;
  const isStandardHrubka = STANDARD_HRUBKY.has(zNum);
  const massiveAvailable = dostupneHrubky.has(zNum);
  // Zobraz prompt ak je dostupné pravidlo (rule) ALEBO ak nie je dostupný
  // masívny materiál pre túto hrúbku (user musí vedieť že to bude lepené).
  const showSplitPrompt =
    druh === "plosny" &&
    Number.isFinite(zNum) &&
    !isStandardHrubka &&
    (splitRule !== null || !massiveAvailable);

  useEffect(() => {
    if (open) {
      // Pri otvorení: synchronizuj druh s režimom (kovanie/normal)
      setDruh(allowedDruhy[0]);
      setSplitChoice("default");
    } else {
      reset({
        nazov_strojovy: "",
        pocet_ks: 1,
        x_mm: "",
        y_mm: "",
        z_mm: "",
      });
      setDruh(allowedDruhy[0]);
      setSlotId(NO_SLOT);
      setDirectMaterialId(null);
      setKovanieNaMetre(false);
      setTycKovania(null);
      setZoznam([]);
      setSplitChoice("default");
    }
    // allowedDruhy je odvodené z režimu — pridávame iba rezim
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rezim, zostavaKod, existingDielce, reset]);

  // Reset voľby pri zmene Z, aby nezostalo nezhodné s novými pravidlami.
  useEffect(() => {
    setSplitChoice("default");
  }, [zNum]);

  /**
   * Výber direct materiálu. Pri kovaní predvyplní prázdny názov podľa
   * kategórie materiálu (Výsuvy, Nohy, Závesy…) — užívateľ ho môže prepísať.
   */
  function handlePickDirectMaterial(id: string | null, m?: Material): void {
    setDirectMaterialId(id);
    setOznacene(id && m ? m : null);
    if (druh === "kusovy") {
      setKovanieNaMetre(Boolean(id && m?.druh === "dlzkovy"));
      setTycKovania(id && m?.x_mm ? m.x_mm : null);
    }
    if (id && m && druh === "kusovy") {
      const current = (getValues("nazov_strojovy") ?? "").trim();
      // Alias, ako si ho user pomenoval, je aj názov riadku (KOLO 176)
      const navrh = predvolenyNazovKovania(m);
      if (!current) setValue("nazov_strojovy", navrh);
    }
  }

  function handleSlotChange(id: string): void {
    setSlotId(id);
    // Hrúbka dielca sa predvyplní z materiálu slotu — ostáva
    // editovateľná (zmena napr. na 36 spustí lepený flow).
    const material = sloty.find((s) => s.id === id)?.material;
    if (druh === "plosny" && material?.z_mm != null) {
      setValue("z_mm", normalizeNum(material.z_mm));
    }
  }

  /**
   * Dvojklik / Enter v tabuľke (okno kovania a cenovky) pošle riadok hneď —
   * stav s vybranou položkou ešte nie je v tomto renderi, preto `materialId`.
   */
  function odoslat(materialId?: string): void {
    void handleSubmit((v) => onSubmit(v, materialId))();
  }

  async function onSubmit(values: FormData, materialIdNavyse?: string): Promise<void> {
    const directMaterialId = materialIdNavyse ?? vybranyMaterialId;
    // Validácia druhu materiálu:
    // - plosny: slot je preferovaný (auto-hrany), direct fallback
    // - dlzkovy/kusovy: vyžaduje direct material_id
    if (druh !== "plosny" && !directMaterialId) {
      toast({
        variant: "destructive",
        title: druh === "cenovka" ? "Vyber hranu" : "Vyber materiál",
        description:
          druh === "cenovka"
            ? "Cenovka sa reže z hrany — vyber ju z cenníka."
            : `Dielec druhu ${DRUH_LABELS[druh]} vyžaduje materiál z cenníka.`,
      });
      return;
    }
    // Kovanie na metre bez dĺžky by stálo 0 € a v náreze chýbalo
    if (kovanieNaMetre && druh === "kusovy" && !(parseFloat(parseDecimal(values.x_mm)) > 0)) {
      toast({
        variant: "destructive",
        title: "Zadaj dĺžku",
        description: "Kovanie sa predáva na metre — dĺžka určuje cenu aj nárezový plán.",
      });
      return;
    }
    // Cenovka bez dĺžky by nespotrebovala hranu ani nič nestála
    if (druh === "cenovka" && !(parseFloat(parseDecimal(values.x_mm)) > 0)) {
      toast({
        variant: "destructive",
        title: "Zadaj dĺžku",
        description: "Dĺžka cenovky určuje, koľko hrany treba objednať.",
      });
      return;
    }

    // Kód auto-generujeme: <zostavaKod>_p<n>. Užívateľ ho nezadáva.
    const autoKod = suggestNextKod(zostavaKod, existingDielce);

    try {
      const z = druh === "plosny" ? values.z_mm || null : null;
      const zSubmit = z ? Math.round(parseFloat(z)) : 0;
      // Rozhodni layers podľa splitChoice. "default" = prvá kombinácia
      // splitRule, "alt:X+Y" = vybraná kombinácia, "masivny" = jeden dielec.
      let submitLayers: number[];
      if (druh !== "plosny" || !splitRule) {
        submitLayers = [zSubmit || 18];
      } else if (splitChoice === "masivny") {
        submitLayers = [zSubmit];
      } else if (splitChoice.startsWith("alt:")) {
        const picked = splitChoice
          .slice(4)
          .split("+")
          .map((n) => parseInt(n, 10))
          .filter((n) => Number.isFinite(n) && n > 0);
        submitLayers =
          picked.length > 0
            ? picked
            : (splitRule.kombinacie[0] ?? [zSubmit]);
      } else {
        submitLayers = splitRule.kombinacie[0] ?? [zSubmit];
      }

      // Lepený dielec: rodič (z = celková hrúbka, rozmery + hrany)
      // + zabalené vrstvy _lep1/_lep2 (dosky, operácia lep_nc, bez hrán).
      const lepeny = submitLayers.length > 1;
      const baseNazov =
        values.nazov_strojovy || (druh === "cenovka" ? NAZOV_CENOVKY : autoKod);
      // Smer vlákien len pri materiáli s drevnou štruktúrou
      const slotMat = sloty.find((s) => s.id === slotId)?.material ?? null;
      const defaultSmer =
        !slotMat || slotMat.smer_vlakien_default !== "0" ? "X" : "0";
      if (viacZostav) {
        // Kovanie / cenovka do viacerých zostáv — ten istý riadok v každej,
        // jeden krok späť; vrstvy nemajú
        const riadok = {
          nazov_strojovy: baseNazov || null,
          operacia: null,
          pocet_ks: values.pocet_ks,
          x_mm:
            druh === "cenovka" || kovanieNaMetre ? parseDecimal(values.x_mm) || null : null,
          y_mm: null,
          z_mm: null,
          material_slot_id: null,
          material_id_direct: directMaterialId,
          nadmiera_x_mm: "0.00",
          nadmiera_y_mm: "0.00",
          nadmiera_aktivna: false,
          smer_vlakien: "0" as const,
          hrana_x1_auto: false,
          hrana_x2_auto: false,
          hrana_y1_auto: false,
          hrana_y2_auto: false,
        };
        const ciele = [
          { id: zostavaId, kod: zostavaKod, existingDielce },
          ...dalsieZostavy,
        ];
        const v = await hromadne.pridajDielce(
          ciele.map((c) => ({
            zostavaId: c.id,
            kod: c.kod,
            data: {
              ...riadok,
              kod: suggestNextKod(c.kod, c.existingDielce),
              poradie: c.existingDielce.length,
            },
          })),
          `pridanie ${druh === "cenovka" ? "cenovky" : "kovania"} do ${ciele.length} zostáv`,
        );
        const pridane = druh === "cenovka" ? "Cenovka pridaná" : "Kovanie pridané";
        toast(
          v.chyby.length === 0
            ? { title: pridane, description: `${baseNazov} → ${kodyZostav.join(", ")}` }
            : {
                variant: "destructive",
                title: `${pridane} do ${v.hotovo.length}, ${v.chyby.length} neprešlo`,
                description: v.chyby.map((c) => `${c.kod}: ${c.sprava}`).join(" · "),
              },
        );
        onOpenChange(false);
        return;
      }
      const parentRes = await createMutation.mutateAsync({
        kod: autoKod,
        nazov_strojovy: baseNazov || null,
        operacia: null,
        pocet_ks: values.pocet_ks,
        x_mm:
          druh === "kusovy" && !kovanieNaMetre ? null : parseDecimal(values.x_mm) || null,
        y_mm: druh === "plosny" ? parseDecimal(values.y_mm) || null : null,
        // Holá doska — x_mm/y_mm s hranou z nej dopočíta backend
        ...(druh === "plosny" && rozmeryBezHran
          ? {
              holy_x_mm: parseDecimal(values.x_mm) || null,
              holy_y_mm: parseDecimal(values.y_mm) || null,
            }
          : {}),
        z_mm: druh === "plosny" ? parseDecimal(values.z_mm) || null : null,
        material_slot_id:
          druh === "plosny" && slotId !== NO_SLOT ? slotId : null,
        material_id_direct: druh !== "plosny" ? directMaterialId : null,
        // Nadmieru dopočíta backend z operácie a hrán (KOLO 104) — do
        // tohto kola tu bolo natvrdo 5,00 pre každý plošný dielec.
        nadmiera_x_mm: "0.00",
        nadmiera_y_mm: "0.00",
        nadmiera_aktivna: druh === "plosny",
        smer_vlakien: druh === "plosny" ? defaultSmer : "0",
        hrana_x1_auto: druh === "plosny",
        hrana_x2_auto: druh === "plosny",
        hrana_y1_auto: druh === "plosny",
        hrana_y2_auto: druh === "plosny",
        poradie: existingDielce.length,
      });
      if (lepeny) {
        for (let li = 0; li < submitLayers.length; li++) {
          await createMutation.mutateAsync({
            kod: kodVrstvy(autoKod, li + 1),
            nazov_strojovy: `${baseNazov}_lep${li + 1}`,
            parent_dielec_id: parentRes.data.id,
            operacia: OPERACIA_LEPENA_VRSTVA,
            pocet_ks: values.pocet_ks,
            // Rozmer rodiča s hranou — vrstva ho nasleduje (KOLO 131);
            // zadaný holý rozmer by ju hneď označil ako výnimku
            x_mm: parentRes.data.x_mm ?? null,
            y_mm: parentRes.data.y_mm ?? null,
            z_mm: String(submitLayers[li]),
            material_slot_id: slotId !== NO_SLOT ? slotId : null,
            material_id_direct: null,
            nadmiera_x_mm: "0.00",
            nadmiera_y_mm: "0.00",
            nadmiera_aktivna: true,
            smer_vlakien: defaultSmer,
            hrana_x1_auto: false,
            hrana_x2_auto: false,
            hrana_y1_auto: false,
            hrana_y2_auto: false,
            poradie: existingDielce.length + 1 + li,
          });
        }
      }
      const msg = lepeny
        ? `Lepený dielec ${values.z_mm} mm (${submitLayers.join("+")}) — ${submitLayers.length} vrstvy`
        : baseNazov;
      toast({ title: "Dielec pridaný", description: msg });
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba",
        description: extractErrorMessage(err),
      });
    }
  }

  /**
   * Kovanie do zoznamu (dvojklik, Enter, tlačidlo Do zoznamu). To isté
   * kusové kovanie druhýkrát zvýši počet; kovanie na metre ide vždy ako
   * nový riadok (iná dĺžka kusa) a kurzor skočí na jeho dĺžku.
   */
  function doZoznamu(m: Material, pocet = 1, nazov?: string, dlzka = ""): void {
    const naMetre = m.druh === "dlzkovy";
    const kluc = naMetre ? `${m.id}-${Date.now()}` : m.id;
    setZoznam((z) => {
      const i = naMetre ? -1 : z.findIndex((p) => p.material.id === m.id);
      if (i >= 0) {
        return z.map((p, j) =>
          j === i ? { ...p, pocet: String((parseInt(p.pocet, 10) || 0) + pocet) } : p,
        );
      }
      return [
        ...z,
        {
          kluc,
          material: m,
          nazov: nazov || predvolenyNazovKovania(m),
          pocet: String(pocet),
          dlzka,
        },
      ];
    });
    if (naMetre && !dlzka) setFokusDlzky(kluc);
  }

  function upravZoznam(kluc: string, zmena: Partial<KovanieVZozname>): void {
    setZoznam((z) => z.map((p) => (p.kluc === kluc ? { ...p, ...zmena } : p)));
  }

  /** Označené kovanie s poľami pod tabuľkou do zoznamu — myšou bez dvojkliku. */
  function oznaceneDoZoznamu(material: Material | undefined): void {
    if (!material) return;
    const v = getValues();
    doZoznamu(
      material,
      Math.max(1, Number(v.pocet_ks) || 1),
      (v.nazov_strojovy ?? "").trim() || undefined,
      kovanieNaMetre ? parseDecimal(v.x_mm ?? "") : "",
    );
    reset({ nazov_strojovy: "", pocet_ks: 1, x_mm: "", y_mm: "", z_mm: "" });
    setDirectMaterialId(null);
    setOznacene(null);
    setKovanieNaMetre(false);
  }

  async function odoslatZoznam(): Promise<void> {
    const chyba = zoznam.find(
      (p) =>
        !(parseInt(p.pocet, 10) >= 1) ||
        (p.material.druh === "dlzkovy" && !(parseFloat(parseDecimal(p.dlzka)) > 0)),
    );
    if (chyba) {
      toast({
        variant: "destructive",
        title: `Doplň ${chyba.material.druh === "dlzkovy" && !(parseFloat(parseDecimal(chyba.dlzka)) > 0) ? "dĺžku" : "počet"} — ${chyba.nazov}`,
        description:
          chyba.material.druh === "dlzkovy"
            ? "Kovanie sa predáva na metre — dĺžka určuje cenu aj nárezový plán."
            : "Počet kusov musí byť aspoň 1.",
      });
      if (chyba.material.druh === "dlzkovy") setFokusDlzky(chyba.kluc);
      return;
    }
    const ciele = [
      { id: zostavaId, kod: zostavaKod, existingDielce },
      ...(viacZostav ? dalsieZostavy : []),
    ];
    setUkladaZoznam(true);
    try {
      const v = await hromadne.pridajDielceNaraz(
        ciele.map((c) => {
          const kody = kodyZaSebou(c.kod, c.existingDielce, zoznam.length);
          return {
            zostavaId: c.id,
            kod: c.kod,
            dielce: zoznam.map((p, i) => ({
              kod: kody[i]!,
              nazov_strojovy: p.nazov.trim() || null,
              operacia: null,
              pocet_ks: parseInt(p.pocet, 10),
              x_mm: p.material.druh === "dlzkovy" ? parseDecimal(p.dlzka) : null,
              y_mm: null,
              z_mm: null,
              material_slot_id: null,
              material_id_direct: p.material.id,
              nadmiera_x_mm: "0.00",
              nadmiera_y_mm: "0.00",
              nadmiera_aktivna: false,
              smer_vlakien: "0" as const,
              hrana_x1_auto: false,
              hrana_x2_auto: false,
              hrana_y1_auto: false,
              hrana_y2_auto: false,
              poradie: c.existingDielce.length + i,
            })),
          };
        }),
        `pridanie ${zoznam.length} ${sklonuj(zoznam.length, ["kovania", "kovaní", "kovaní"])}${ciele.length > 1 ? ` do ${ciele.length} zostáv` : ""}`,
      );
      toast(
        v.chyby.length === 0
          ? {
              title: `Pridané ${zoznam.length} ${sklonuj(zoznam.length, ["kovanie", "kovania", "kovaní"])}`,
              description: `${zoznam.map((p) => p.nazov).join(", ")} → ${ciele.map((c) => c.kod).join(", ")}`,
            }
          : {
              variant: "destructive",
              title: `Kovanie neprešlo do ${v.chyby.length} ${sklonuj(v.chyby.length, ["zostavy", "zostáv", "zostáv"])}`,
              description: v.chyby.map((c) => `${c.kod}: ${c.sprava}`).join(" · "),
            },
      );
      if (v.chyby.length === 0) onOpenChange(false);
    } finally {
      setUkladaZoznam(false);
    }
  }

  const nadpis = viacZostav
    ? `Pridať ${coPridava} do ${kodyZostav.length} zostáv (${kodyZostav.join(", ")})`
    : rezim === "dielec"
      ? "Pridať dielec"
      : `Pridať ${coPridava}`;

  // Kovanie a cenovka: ten istý veľký rám ako Zmeniť kovanie či výber
  // hrany (KOLO 210 — user: „tabuľka je veľmi malá, nech je vždy rovnaká"),
  // polia riadku pod tabuľkou
  if (rezim !== "dielec") {
    const cenovka = druh === "cenovka";
    const zoznamKovani = !cenovka && zoznam.length > 0;
    return (
      <VyberDialog
        open={open}
        onOpenChange={onOpenChange}
        nadpis={nadpis}
        popis={
          cenovka
            ? "Cenovka sa reže z hrany — objedná sa ako hrana. Dole zadaj dĺžku a počet."
            : "Dvojklik alebo Enter pridá kovanie do zoznamu dole — vyber ďalšie a pridaj všetky naraz."
        }
        vybraneId={vybranyMaterialId}
        potvrdText={
          isSubmitting || ukladaZoznam
            ? "Pridáva sa…"
            : zoznamKovani
              ? `Pridať ${zoznam.length} ${sklonuj(zoznam.length, ["kovanie", "kovania", "kovaní"])}`
              : "Pridať"
        }
        potvrdZakazane={isSubmitting || ukladaZoznam}
        onPotvrd={() => (zoznamKovani ? void odoslatZoznam() : odoslat())}
        akcie={
          zoznamKovani ? (
            <ZoznamKovani
              zoznam={zoznam}
              onZmena={upravZoznam}
              onOdober={(kluc) => setZoznam((z) => z.filter((p) => p.kluc !== kluc))}
              onOdoslat={() => void odoslatZoznam()}
              oznacene={oznacene}
              onOznaceneDoZoznamu={() => oznaceneDoZoznamu(oznacene ?? undefined)}
            />
          ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              odoslat();
            }}
            className="flex w-full flex-wrap items-end gap-3"
          >
            <div className="min-w-[12rem] flex-1 space-y-1">
              <Label htmlFor="dc-nazov">Názov</Label>
              <Input
                id="dc-nazov"
                autoComplete="off"
                placeholder={cenovka ? NAZOV_CENOVKY : "noha_AXILO_100 / záves CLIP top"}
                {...register("nazov_strojovy")}
              />
            </div>
            {cenovka || kovanieNaMetre ? (
              <div className="w-36 space-y-1">
                <Label htmlFor={cenovka ? "dc-x" : "dc-dlzka-kovania"}>
                  {cenovka ? "Dĺžka (mm)" : "Dĺžka kusa (mm) *"}
                </Label>
                <Input
                  id={cenovka ? "dc-x" : "dc-dlzka-kovania"}
                  inputMode="decimal"
                  placeholder={cenovka ? "865" : "1200"}
                  {...register("x_mm")}
                />
              </div>
            ) : null}
            <div className="w-28 space-y-1">
              <Label htmlFor="dc-ks">Počet kusov</Label>
              <Input id="dc-ks" type="number" min={1} {...register("pocet_ks")} />
            </div>
            {!cenovka ? (
              <Button
                type="button"
                variant="outline"
                disabled={!oznacene}
                onClick={() => oznaceneDoZoznamu(oznacene ?? undefined)}
                title="Pridá označené kovanie do zoznamu — potom vyber ďalšie a pridaj všetky naraz"
              >
                Do zoznamu
              </Button>
            ) : null}
            {/* Enter v poli pošle riadok */}
            <button type="submit" className="hidden" tabIndex={-1} aria-hidden="true" />
            {kovanieNaMetre && !cenovka ? (
              <p className="basis-full text-xs text-muted-foreground">
                Kovanie sa predáva na metre a kráti sa z tyče
                {tycKovania ? ` ${tycKovania.toLocaleString("sk-SK")} mm` : ""} — kus
                pôjde do nárezového plánu.
                {tycKovania ? null : " Dĺžku tyče cenník nepozná, doplň ju v Cenníku."}
              </p>
            ) : null}
          </form>
          )
        }
      >
        {cenovka ? (
          <MaterialSelect
            vOkne
            value={vybranyMaterialId}
            onChange={(id, m) => handlePickDirectMaterial(id, m)}
            // Dvojklik vyberie, dĺžku treba dopísať
            onPotvrd={(m) => {
              handlePickDirectMaterial(m.id, m as Material);
              document.getElementById("dc-x")?.focus();
            }}
            druh="dlzkovy"
            sekcia="hrany"
            placeholder="Vyhľadať hranu (8681_22x1, 22x05, …)…"
          />
        ) : (
          <KovanieCategoryPicker
            vOkne
            value={vybranyMaterialId}
            onChange={(id, m) => handlePickDirectMaterial(id, m)}
            // Dvojklik / Enter pridá do zoznamu (KOLO 226) — kovanie na
            // metre dostane v zozname pole dĺžky s kurzorom
            onPotvrd={(m) => doZoznamu(m as Material)}
          />
        )}
      </VyberDialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{nadpis}</DialogTitle>
          <DialogDescription className="sr-only">
            Nový riadok kusovníka — rozmery, materiál, hrany a operácia.
          </DialogDescription>
        </DialogHeader>
        {/* min-w-0: DialogContent je grid — bez toho nezalomiteľné názvy
            kovaní (Blum_vys_…) roztiahnu form za okraj dialógu */}
        <form onSubmit={handleSubmit((v) => onSubmit(v))} className="min-w-0 space-y-4">
          {/* Druh dropdown: skrytý v kovanie režime (vždy kusový), inak iba povolené hodnoty */}
          {allowedDruhy.length > 1 ? (
            <div className="space-y-2">
              <Label htmlFor="dc-druh">Druh dielca *</Label>
              <Select value={druh} onValueChange={(v) => zmenDruh(v as DielecDruh)}>
                <SelectTrigger id="dc-druh" aria-label="Druh dielca">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {allowedDruhy.map((d) => (
                    <SelectItem key={d} value={d}>
                      {DRUH_LABELS[d]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          <Separator />

          <div className="space-y-1.5">
            <Label htmlFor="dc-nazov">Názov</Label>
            <Input
              id="dc-nazov"
              autoFocus
              autoComplete="off"
              list={druh === "plosny" ? "dc-nazov-list" : undefined}
              placeholder={
                druh === "plosny"
                  ? "bok_L / dno / chrbat / police…"
                  : druh === "dlzkovy"
                    ? "hranol_50x50 / pracovná_doska_kasa"
                    : druh === "cenovka"
                      ? NAZOV_CENOVKY
                      : "noha_AXILO_100 / záves CLIP top"
              }
              {...register("nazov_strojovy")}
            />
            {druh === "plosny" ? (
              <datalist id="dc-nazov-list">
                {nazvyDielcov.map((n) => (
                  <option key={n.id} value={n.nazov} />
                ))}
              </datalist>
            ) : null}
          </div>

          {/* Rozmery — dynamicky podľa druhu */}
          {druh === "plosny" ? (
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="dc-x">X (mm)</Label>
                <Input id="dc-x" {...register("x_mm")} placeholder="720" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="dc-y">Y (mm)</Label>
                <Input id="dc-y" {...register("y_mm")} placeholder="500" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="dc-z">Z (mm)</Label>
                <Input id="dc-z" {...register("z_mm")} placeholder="18" />
              </div>
            </div>
          ) : dlzkovyRiadok ? (
            <div className="space-y-1.5">
              <Label htmlFor="dc-x">Dĺžka (mm)</Label>
              <Input
                id="dc-x"
                {...register("x_mm")}
                placeholder={druh === "cenovka" ? "865" : "1500"}
              />
            </div>
          ) : null}

          {/* Delenie hrúbky — prompt pri neštandardnej hrúbke. */}
          {showSplitPrompt ? (
            <SplitPrompt
              zMm={parseFloat(watchedZ)}
              rule={splitRule}
              massiveAvailable={massiveAvailable}
              choice={splitChoice}
              onChange={setSplitChoice}
            />
          ) : null}

          <Separator />

          {/* Materiál — slot pre plošný, direct pre ostatné */}
          {druh === "plosny" ? (
            <div className="space-y-1.5">
              <Label htmlFor="dc-slot">Materiál zákazky</Label>
              <Select value={slotId} onValueChange={handleSlotChange}>
                <SelectTrigger id="dc-slot" aria-label="Materiál zákazky">
                  <SelectValue placeholder="(žiadny)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_SLOT}>(žiadny)</SelectItem>
                  {sloty.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.slot_kod}
                      {s.material ? ` — ${s.material.nazov}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Materiál zákazky (M1, M2…) drží dosku aj hrany. Bez neho =
                bez ceny materiálu.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <Label>
                {druh === "cenovka"
                  ? "Hrana * (cenovka sa reže z hrany — objedná sa ako hrana)"
                  : `Materiál * (${druh === "dlzkovy" ? "hranol / pracovná doska" : "kovanie"})`}
              </Label>
              {druh === "kusovy" ? (
                // Kovanie — výber podľa kategórií (obľúbené + sety),
                // zamestnanec nemusí poznať ID
                <KovanieCategoryPicker
                  value={vybranyMaterialId}
                  onChange={(id, m) => handlePickDirectMaterial(id, m)}
                />
              ) : druh === "cenovka" ? (
                // Ten istý zoznam ako hrana na stranu dielca (sekcia Hrany)
                <MaterialSelect
                  value={vybranyMaterialId}
                  onChange={(id, m) => handlePickDirectMaterial(id, m)}
                  druh="dlzkovy"
                  sekcia="hrany"
                  placeholder="Vyhľadať hranu (8681_22x1, 22x05, …)…"
                />
              ) : (
                <MaterialSelect
                  value={vybranyMaterialId}
                  onChange={(id, m) => handlePickDirectMaterial(id, m)}
                  druh="dlzkovy"
                  // Hranol, pracovná doska aj dĺžkové kovanie (LED profil,
                  // tyč, krytky v m) — cez „Pridať kovanie" by sa uložili
                  // bez dĺžky za 0 €. Hrany majú svoj výber, sklo server
                  // do výberov nepustí.
                  bezHran
                />
              )}
            </div>
          )}

          {druh === "kusovy" && kovanieNaMetre ? (
            <div className="space-y-1.5">
              <Label htmlFor="dc-dlzka-kovania">Dĺžka kusa (mm) *</Label>
              <Input
                id="dc-dlzka-kovania"
                inputMode="decimal"
                placeholder="1200"
                {...register("x_mm")}
              />
              <p className="text-xs text-muted-foreground">
                Kovanie sa predáva na metre a kráti sa z tyče
                {tycKovania ? ` ${tycKovania.toLocaleString("sk-SK")} mm` : ""} — kus
                pôjde do nárezového plánu.
                {tycKovania ? null : " Dĺžku tyče cenník nepozná, doplň ju v Cenníku."}
              </p>
            </div>
          ) : null}

          <Separator />

          <div className="space-y-1.5">
            <Label htmlFor="dc-ks">Počet kusov</Label>
            <Input id="dc-ks" type="number" min={1} {...register("pocet_ks")} />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Zrušiť
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Pridáva sa…" : "Pridať"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Prompt: ako vyrobiť dielec neštandardnej hrúbky.
 *
 * Tri možnosti:
 * 1. **Lepený (default)** — prvá kombinácia zo split rule (napr. 18+18)
 * 2. **Lepený (iná kombinácia)** — dropdown s ostatnými kombináciami,
 *    aktívne iba ak rule má aspoň 2 kombinácie
 * 3. **Masívny** — jeden dielec X mm, iba ak existuje materiál tej hrúbky
 */
function SplitPrompt({
  zMm,
  rule,
  massiveAvailable,
  choice,
  onChange,
}: {
  zMm: number;
  rule: SplitRule | null;
  massiveAvailable: boolean;
  choice: string;
  onChange: (v: string) => void;
}): JSX.Element {
  const defaultCombo = rule?.kombinacie[0];
  const altCombos = (rule?.kombinacie ?? []).slice(1);
  // Aktuálne vybraná alt kombinácia (ak choice = "alt:X+Y")
  const selectedAltKey = choice.startsWith("alt:") ? choice.slice(4) : null;
  const altKeys = altCombos.map(comboKey);
  // Ak žiadna alt nie je vybraná, default na prvú alt key (pre dropdown).
  const altDropdownValue = selectedAltKey ?? altKeys[0] ?? "";

  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2">
      <p className="text-sm font-medium">
        Hrúbka {zMm} mm nie je štandardná. Ako vyrobiť dielec?
      </p>

      {defaultCombo ? (
        <label className="flex items-start gap-2 text-sm cursor-pointer">
          <input
            type="radio"
            name="split-choice"
            checked={choice === "default"}
            onChange={() => onChange("default")}
            className="mt-0.5"
          />
          <span>
            <span className="font-medium">Lepený —</span>{" "}
            {defaultCombo.join(" + ")} mm
            <span className="text-xs text-muted-foreground"> (default)</span>
          </span>
        </label>
      ) : null}

      {altCombos.length > 0 ? (
        <label className="flex items-start gap-2 text-sm cursor-pointer">
          <input
            type="radio"
            name="split-choice"
            checked={choice.startsWith("alt:")}
            onChange={() => onChange(`alt:${altDropdownValue}`)}
            className="mt-0.5"
          />
          <span className="flex items-center gap-2">
            <span className="font-medium">Lepený (iná kombinácia) —</span>
            <Select
              value={altDropdownValue}
              onValueChange={(v) => onChange(`alt:${v}`)}
            >
              <SelectTrigger className="h-7 w-32 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {altCombos.map((combo) => (
                  <SelectItem key={comboKey(combo)} value={comboKey(combo)}>
                    {combo.join(" + ")} mm
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </span>
        </label>
      ) : null}

      {massiveAvailable ? (
        <label className="flex items-start gap-2 text-sm cursor-pointer">
          <input
            type="radio"
            name="split-choice"
            checked={choice === "masivny"}
            onChange={() => onChange("masivny")}
            className="mt-0.5"
          />
          <span>
            <span className="font-medium">Masívny —</span> jeden dielec {zMm} mm
          </span>
        </label>
      ) : (
        <p className="text-xs text-muted-foreground italic">
          Masívny {zMm} mm materiál v cenníku nie je — možný iba lepený.
        </p>
      )}

      {!rule ? (
        <p className="text-xs text-amber-700">
          Pre túto hrúbku zatiaľ neexistuje pravidlo delenia. Vytvor ho v Nastaveniach → Dielce a dosky → Lepené dielce, alebo
          sa vyrobí masívne.
        </p>
      ) : null}
    </div>
  );
}

/**
 * Zoznam kovaní pod tabuľkou výberu (KOLO 226) — názov, počet, pri kovaní
 * na metre dĺžka kusa, ✕. Pridať N kovaní ich vloží naraz.
 */
function ZoznamKovani({
  zoznam,
  onZmena,
  onOdober,
  onOdoslat,
  oznacene,
  onOznaceneDoZoznamu,
}: {
  zoznam: KovanieVZozname[];
  onZmena: (kluc: string, zmena: Partial<KovanieVZozname>) => void;
  onOdober: (kluc: string) => void;
  onOdoslat: () => void;
  oznacene: Material | null;
  onOznaceneDoZoznamu: () => void;
}): JSX.Element {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onOdoslat();
      }}
      className="w-full space-y-1.5"
      aria-label="Kovanie na pridanie"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-muted-foreground">
          Na pridanie ({zoznam.length})
        </span>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-6 px-2 text-xs"
          disabled={!oznacene}
          onClick={onOznaceneDoZoznamu}
          title="Pridá označené kovanie do zoznamu"
        >
          + Do zoznamu
        </Button>
      </div>
      <ul className="max-h-36 space-y-1 overflow-y-auto">
        {zoznam.map((p) => {
          const naMetre = p.material.druh === "dlzkovy";
          return (
            <li key={p.kluc} className="flex items-center gap-2">
              <Input
                aria-label={`Názov riadku ${p.material.nazov}`}
                value={p.nazov}
                onChange={(e) => onZmena(p.kluc, { nazov: e.target.value })}
                className="h-7 min-w-0 flex-1 text-xs"
                title={p.material.nazov}
              />
              {naMetre ? (
                <Input
                  id={`dc-zoznam-dlzka-${p.kluc}`}
                  aria-label={`Dĺžka kusa (mm) ${p.material.nazov}`}
                  inputMode="decimal"
                  placeholder="dĺžka mm"
                  value={p.dlzka}
                  onChange={(e) => onZmena(p.kluc, { dlzka: e.target.value })}
                  className="h-7 w-24 text-xs"
                />
              ) : null}
              <Input
                aria-label={`Počet ${p.material.nazov}`}
                type="number"
                min={1}
                value={p.pocet}
                onChange={(e) => onZmena(p.kluc, { pocet: e.target.value })}
                className="h-7 w-16 text-xs"
              />
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                aria-label={`Odobrať ${p.material.nazov}`}
                onClick={() => onOdober(p.kluc)}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </li>
          );
        })}
      </ul>
      {/* Enter v poli pošle celý zoznam */}
      <button type="submit" className="hidden" tabIndex={-1} aria-hidden="true" />
    </form>
  );
}
